import React from 'react';
import * as Sentry from '@sentry/react';

// React Error Boundaries must be class components — hooks cannot catch render errors.
//
// Two export variants:
//   <AppErrorBoundary>   — top-level; full-screen fallback, used in index.js
//   <PageErrorBoundary>  — in-app; inline fallback, used in App.js around page content
//                          so the sidebar stays visible when a single page crashes

// ── Shared error state logic ──────────────────────────────────────────────────
class ErrorBoundaryBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Report to Sentry in production (SDK is no-op when enabled:false in dev)
    Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });

    if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary] Caught render error:', error);
      console.error('[ErrorBoundary] Component stack:', info?.componentStack);
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }
}

// ── Top-level boundary — full-screen fallback ─────────────────────────────────
export class AppErrorBoundary extends ErrorBoundaryBase {
  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f7f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        <div style={{
          background: '#fff',
          border: '1px solid #e4e0d9',
          borderRadius: 14,
          padding: '48px 40px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,.06)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e1d1a', marginBottom: 10 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, color: '#52504a', lineHeight: 1.7, marginBottom: 8 }}>
            The app encountered an unexpected error. Please refresh the page — your data is safe.
          </p>
          <p style={{ fontSize: 13, color: '#7c776f', marginBottom: 28 }}>
            If the problem persists, contact support at{' '}
            <a href="mailto:liam@skucommand.com" style={{ color: '#4d6b0f', textDecoration: 'none' }}>
              liam@skucommand.com
            </a>
          </p>

          {/* Show error detail in development only */}
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 11,
              color: '#dc2626',
              textAlign: 'left',
              overflowX: 'auto',
              marginBottom: 24,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
              {this.state.error.toString()}
            </pre>
          )}

          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#BDEB24',
              color: '#0C0F0D',
              border: 'none',
              borderRadius: 8,
              padding: '10px 28px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              marginRight: 10,
            }}
          >
            Refresh page
          </button>
          <button
            onClick={() => this.handleReset()}
            style={{
              background: 'transparent',
              color: '#52504a',
              border: '1px solid #e4e0d9',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

// ── Page-level boundary — inline fallback (sidebar stays visible) ─────────────
export class PageErrorBoundary extends ErrorBoundaryBase {
  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        padding: '48px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}>
        <div style={{
          background: '#fff',
          border: '1px solid #fecaca',
          borderRadius: 12,
          padding: '32px 28px',
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e1d1a', marginBottom: 8 }}>
            This page crashed
          </h2>
          <p style={{ fontSize: 13, color: '#52504a', lineHeight: 1.65, marginBottom: 6 }}>
            Something went wrong loading this page. Your data is safe.
          </p>
          <p style={{ fontSize: 12, color: '#7c776f', marginBottom: 20 }}>
            Contact{' '}
            <a href="mailto:liam@skucommand.com" style={{ color: '#4d6b0f' }}>
              liam@skucommand.com
            </a>{' '}
            if this keeps happening.
          </p>

          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              padding: '10px 12px',
              fontSize: 11,
              color: '#dc2626',
              textAlign: 'left',
              overflowX: 'auto',
              marginBottom: 16,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
              {this.state.error.toString()}
            </pre>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              onClick={() => this.handleReset()}
              style={{
                background: '#BDEB24',
                color: '#0C0F0D',
                border: 'none',
                borderRadius: 7,
                padding: '8px 20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'transparent',
                color: '#52504a',
                border: '1px solid #e4e0d9',
                borderRadius: 7,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }
}
